/**
 * Staff — Delivered Archive.
 *
 * Once a delivered order has sat in the "Delivered" column of the Orders Board
 * for 14 days, the nightly scheduler flips `archived=true` on it. The Orders
 * Board hides these rows so the delivered column doesn't grow forever, but the
 * record itself is kept in Mongo permanently — this page is where staff look
 * them up.
 *
 * Read-only by design: no restore, no delete. If you truly need to remove a
 * record it should go via the Recently Deleted flow.
 *
 * Backend: GET /api/staff/orders/delivered-archive
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, PackageCheck, Search, X } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import useHomePath from '@/hooks/useHomePath';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const BRAND = {
  probound:       { label: 'ProBound',       colour: '#2F5E96', ink: '#fff' },
  prodecor:       { label: 'ProDecor',       colour: '#D5AB09', ink: '#2B2101' },
  proepoxy:       { label: 'ProEpoxy',       colour: '#7A0B7A', ink: '#fff' },
  material_order: { label: 'Material Order', colour: '#1f4030', ink: '#fff' },
};

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '—');

export default function DeliveredArchive() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/orders/delivered-archive`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load archive');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) =>
      (o.reference || '').toLowerCase().includes(q)
      || (o.customer_reference || '').toLowerCase().includes(q)
      || (o.installer_name || '').toLowerCase().includes(q)
      || (o.company_name || '').toLowerCase().includes(q)
      || (o.invoice_number || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/staff/order-processing')}
            data-testid="delivered-archive-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Orders Board"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <PackageCheck className="w-5 h-5 shrink-0" /> Delivered Archive
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              Deliveries auto-archived after 14 days · {rows.length} record{rows.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="relative shrink-0">
            <Search className="w-4 h-4 text-white/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reference, customer, invoice…"
              data-testid="delivered-archive-search"
              className="pl-9 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 text-white w-56 md:w-72 focus:outline-none focus:bg-white/20 focus:border-white/40"
              aria-label="Search delivered archive"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center" data-testid="delivered-archive-empty">
            <PackageCheck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">
              {search.trim() ? 'No matches' : 'Nothing archived yet'}
            </h3>
            <p className="text-gray-500 text-sm">
              Orders drop off the Delivered column and land here automatically after 14 days.
            </p>
          </Card>
        ) : (
          filtered.map((order) => {
            const brand = BRAND[order.product] || {};
            const detailProduct = order.product === 'material_order' ? 'probound' : order.product;
            return (
              <Card
                key={order.id}
                className="p-4 hover:shadow-md transition cursor-pointer"
                onClick={() => navigate(order.product === 'material_order' ? `/material-orders/${order.id}` : `/${detailProduct}/orders/${order.id}`)}
                data-testid={`archive-row-${order.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: brand.colour, color: brand.ink }}
                      >
                        {brand.label || order.product}
                      </span>
                      <span className="font-mono text-xs text-gray-700">{order.reference || order.id?.slice(0, 8)}</span>
                      {order.invoice_number && (
                        <span className="text-[10px] font-mono text-indigo-700">Inv: {order.invoice_number}</span>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-gray-900 mt-1 truncate">
                      {order.customer_reference || order.installer_name || 'No reference'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {order.company_name ? `${order.company_name} · ` : ''}
                      {fmtGBP(order.total)} · Delivered {fmtDate(order.delivered_at)} · Archived {fmtDate(order.archived_at)}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
